import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ChevronDown, LogOut, Menu, Settings, ShoppingCart, User, X } from 'lucide-react';
import { logout } from '../features/auth/authSlice';
import LanguageSelector from './LanguageSelector';
import BrandLogo from './BrandLogo';
import { Badge, Button } from './ui';

const Header = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.auth);
  const { cartItems } = useSelector((state) => state.cart);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 12);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleContactClick = (e) => {
    e.preventDefault();

    if (location.pathname !== '/') {
      navigate('/', {
        state: { scrollToContact: true },
        replace: true,
      });
    } else {
      const contactSection = document.getElementById('contact-section');
      if (contactSection) {
        contactSection.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }
    setIsMobileMenuOpen(false);
  };

  useEffect(() => {
    if (location.pathname === '/' && location.state?.scrollToContact) {
      const contactSection = document.getElementById('contact-section');
      if (contactSection) {
        setTimeout(() => {
          contactSection.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }, 100);
      }
      navigate('/', {
        state: {},
        replace: true,
      });
    }
  }, [location, navigate]);

  const logoutHandler = () => {
    dispatch(logout());
    navigate('/login');
  };

  const navItems = [
    { name: t('nav.indicators'), path: '/indicators' },
    { name: t('nav.free'), path: '/free' },
    { name: t('nav.resources'), path: '/resources' },
    { name: t('nav.ninjatrader'), path: '/ninjatrader' },
    { name: t('footer.links.contact'), path: '#contact', onClick: handleContactClick },
  ];

  const isNavActive = (path) =>
    path === '/resources'
      ? location.pathname === '/resources' || location.pathname.startsWith('/resources/')
      : location.pathname === path;

  const cartLabel =
    cartItems?.length > 0 ? `${t('nav.cart')} (${cartItems.length})` : t('nav.cart');

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        isScrolled || isMobileMenuOpen
          ? 'border-b border-white/[.06] bg-bg/80 backdrop-blur-xl'
          : 'border-b border-transparent bg-transparent'
      }`}
    >
      <div className="mx-auto flex h-20 max-w-page items-center justify-between px-6 md:h-24">
        <BrandLogo className="h-14 md:h-16" />

        <nav className="hidden items-center gap-8 md:flex">
          <div className="flex items-center gap-6">
            {navItems.map((item) =>
              item.onClick ? (
                <a
                  key={item.name}
                  href={item.path}
                  onClick={item.onClick}
                  className="cursor-pointer text-[13px] text-muted transition-colors hover:text-body"
                >
                  {item.name}
                </a>
              ) : (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`text-[13px] transition-colors hover:text-body ${
                    isNavActive(item.path) ? 'text-body' : 'text-muted'
                  }`}
                >
                  {item.name}
                </Link>
              )
            )}
          </div>

          <div className="flex items-center gap-4">
            <LanguageSelector />

            <Link
              to="/cart"
              className="relative text-muted transition-colors hover:text-body"
              aria-label={cartLabel}
            >
              <ShoppingCart className="h-4 w-4" aria-hidden="true" />
              {cartItems?.length > 0 && (
                <Badge className="absolute -right-2.5 -top-2.5 flex h-4 min-w-4 items-center justify-center !border-0 !bg-body !p-0 !text-[10px] !text-bg">
                  {cartItems.length}
                </Badge>
              )}
            </Link>

            {userInfo ? (
              <div className="relative" ref={menuRef}>
                <button
                  type="button"
                  onClick={() => setIsOpen(!isOpen)}
                  className="flex items-center gap-1.5 text-[13px] text-muted transition-colors hover:text-body"
                >
                  <User className="h-3.5 w-3.5" aria-hidden="true" />
                  <span>{userInfo.name}</span>
                  <ChevronDown className="h-3 w-3" aria-hidden="true" />
                </button>

                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 6 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-0 mt-2 w-52 rounded-card border border-white/[.08] bg-surface/95 py-1 shadow-xl backdrop-blur-xl"
                    >
                      <div className="border-b border-white/[.06] px-3.5 py-2.5">
                        <p className="text-sm font-medium text-body">{userInfo.name}</p>
                        <p className="text-xs text-muted">{userInfo.email}</p>
                      </div>
                      <Link
                        to="/profile"
                        className="flex items-center gap-2 px-3.5 py-2 text-[13px] text-muted transition-colors hover:bg-white/[.04] hover:text-body"
                        onClick={() => setIsOpen(false)}
                      >
                        <User className="h-3.5 w-3.5" aria-hidden="true" />
                        {t('nav.profile')}
                      </Link>
                      {userInfo.isAdmin && (
                        <Link
                          to="/admin"
                          className="flex items-center gap-2 px-3.5 py-2 text-[13px] text-muted transition-colors hover:bg-white/[.04] hover:text-body"
                          onClick={() => setIsOpen(false)}
                        >
                          <Settings className="h-3.5 w-3.5" aria-hidden="true" />
                          Admin Panel
                        </Link>
                      )}
                      <div className="mt-1 border-t border-white/[.06]">
                        <button
                          type="button"
                          onClick={logoutHandler}
                          className="flex w-full items-center gap-2 px-3.5 py-2 text-[13px] text-muted transition-colors hover:bg-white/[.04] hover:text-body"
                        >
                          <LogOut className="h-3.5 w-3.5" aria-hidden="true" />
                          {t('nav.logout')}
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Button variant="primary" to="/login" className="!px-3.5 !py-1.5 !text-[13px]">
                {t('nav.login')}
              </Button>
            )}
          </div>
        </nav>

        <div className="flex items-center gap-3 md:hidden">
          <LanguageSelector />

          <Link
            to="/cart"
            className="relative text-muted transition-colors hover:text-body"
            aria-label={cartLabel}
          >
            <ShoppingCart className="h-4 w-4" aria-hidden="true" />
            {cartItems?.length > 0 && (
              <Badge className="absolute -right-2.5 -top-2.5 flex h-4 min-w-4 items-center justify-center !border-0 !bg-body !p-0 !text-[10px] !text-bg">
                {cartItems.length}
              </Badge>
            )}
          </Link>

          <button
            type="button"
            className="text-muted transition-colors hover:text-body"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Menu"
          >
            {isMobileMenuOpen ? (
              <X className="h-5 w-5" aria-hidden="true" />
            ) : (
              <Menu className="h-5 w-5" aria-hidden="true" />
            )}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="border-t border-white/[.06] bg-bg/95 backdrop-blur-xl md:hidden"
          >
            <nav className="mx-auto flex max-w-page flex-col gap-1 px-6 py-4">
              <Link
                to="/indicators"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`rounded-btn px-2 py-2.5 text-[15px] transition-colors ${
                  location.pathname === '/indicators' ? 'text-body' : 'text-muted hover:text-body'
                }`}
              >
                {t('nav.indicators')}
              </Link>
              <Link
                to="/free"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`rounded-btn px-2 py-2.5 text-[15px] transition-colors ${
                  location.pathname === '/free' ? 'text-body' : 'text-muted hover:text-body'
                }`}
              >
                {t('nav.free')}
              </Link>
              <Link
                to="/resources"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`rounded-btn px-2 py-2.5 text-[15px] transition-colors ${
                  isNavActive('/resources') ? 'text-body' : 'text-muted hover:text-body'
                }`}
              >
                {t('nav.resources')}
              </Link>
              <Link
                to="/ninjatrader"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`rounded-btn px-2 py-2.5 text-[15px] transition-colors ${
                  location.pathname === '/ninjatrader' ? 'text-body' : 'text-muted hover:text-body'
                }`}
              >
                {t('nav.ninjatrader')}
              </Link>
              <a
                href="#contact-section"
                onClick={(e) => {
                  handleContactClick(e);
                  setIsMobileMenuOpen(false);
                }}
                className="rounded-btn px-2 py-2.5 text-[15px] text-muted transition-colors hover:text-body"
              >
                {t('footer.links.contact')}
              </a>
              {userInfo && (
                <>
                  <Link
                    to="/profile"
                    className="rounded-btn px-2 py-2.5 text-[15px] text-muted transition-colors hover:text-body"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {t('nav.profile')}
                  </Link>
                  <Link
                    to="/settings"
                    className="rounded-btn px-2 py-2.5 text-[15px] text-muted transition-colors hover:text-body"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Paramètres
                  </Link>
                  <button
                    type="button"
                    onClick={() => {
                      logoutHandler();
                      setIsMobileMenuOpen(false);
                    }}
                    className="rounded-btn px-2 py-2.5 text-left text-[15px] text-muted transition-colors hover:text-body"
                  >
                    {t('nav.logout')}
                  </button>
                </>
              )}
              {!userInfo && (
                <Button
                  variant="primary"
                  to="/login"
                  className="mt-2 w-full"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {t('nav.login')}
                </Button>
              )}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
